// @flow

import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { toast } from "react-toastify";
import { SectionComponent, View, ConfirmationModal } from "@components";
import sharedActionCreators from "@modules/shared/actionCreator";
import serviceActionCreators from "@modules/service/actionCreator";
import * as selectors from "@modules/shared/selectors/employee-absences";
import {
  AddEmployeeAbsenceModal,
  TransferAbsenceModal,
  AbsenceCancelReasonsModal,
} from "@modules/shared/components";
import type { State } from "@store/reducers";
import type {
  EmployeeAbsence,
  AddEmployeeAbsenceModal as AddEmployeeAbsenceModalType,
  TransferAbsenceModalType,
} from "@modules/shared/reducer/employee-absences/types";
import type { EmployeeType } from "@modules/employee/types";
import * as Paths from "@routes/constants";
import type { PropertiesSchema, PaginationExtendedType } from "@types";
import { getEmployee } from "@modules/employee/selectors";
import { getEmployeePositions } from "@modules/shared/selectors/employee-positions.js";
import type { RoleSummary } from "@modules/employee/reducer/employment-details/types";
import AbsencesFilter from "../absences-filter";
import { EmployeeAbsenceHistoryTable } from "@modules/reporting/components";
import * as accountSelectors from "@modules/account/selectors";
import type { ManagerSelfServiceSettings } from "@modules/service/reducer/manager-self-service/types";
import { selectManagerSelfServiceSettings } from "@modules/service/selectors";

type OwnProps = $Exact<{
  employeeId: number,
  clientId: number,
  absenceDetailsRoute: $Keys<typeof Paths>,
  withFilters?: boolean,
}>;

type PropsFromState = $Exact<{
  absenceShiftPattern: Array<Object>,
  absences: Array<EmployeeAbsence>,
  absence: EmployeeAbsence | null,
  addAbsenceModal: AddEmployeeAbsenceModalType,
  transferAbsenceModal: TransferAbsenceModalType,
  absencesSchema: ?PropertiesSchema,
  pagination: PaginationExtendedType,
  employee: EmployeeType,
  employeePositions: RoleSummary[],
  isFirstCareUser: boolean,
  currentUserType: string,
  currentUserPermissions: { [permission: string]: boolean } | null,
  absenceCancelReasons: Array<{ const: number, title: string }>,
  managerSelfServiceSettings: ManagerSelfServiceSettings,
  canAddAbsence: boolean,
  canUpdateAbsence: boolean,
  canCancelAbsence: boolean,
  canDeleteAbsence: boolean,
  canTransferAbsence: boolean,
}>;

type PropsFromDispatch = $Exact<{
  getEmployeeAbsence: typeof sharedActionCreators.GetEmployeeAbsence,
  getEmployeeAbsences: typeof sharedActionCreators.GetEmployeeAbsences,
  deleteEmployeeAbsence: typeof sharedActionCreators.DeleteEmployeeAbsence,
  cancelEmployeeAbsence: typeof sharedActionCreators.CancelEmployeeAbsence,
  getAbsenceCancelReasons: typeof sharedActionCreators.GetAbsenceCancelReasons,
  toggleAddAbsenceModal: typeof sharedActionCreators.ToggleAddAbsenceModal,
  toggleTransferAbsenceModal: typeof sharedActionCreators.ToggleTransferAbsenceModal,
  updateAbsenceAddModalData: typeof sharedActionCreators.UpdateAbsenceAddModalData,
  getEmployeeAbsencesSchema: typeof sharedActionCreators.GetEmployeeAbsencesSchema,
  createEmployeeAbsence: typeof sharedActionCreators.CreateEmployeeAbsence,
  updateEmployeeAbsence: typeof sharedActionCreators.UpdateEmployeeAbsence,
  getEmployeePositions: typeof sharedActionCreators.GetSharedEmployeePositions,
  selectEmployee: typeof sharedActionCreators.SelectEmployee,
  selectEmployeePosition: typeof sharedActionCreators.SelectEmployeePosition,

  getAbsenceTimeLost: typeof sharedActionCreators.GetAbsenceTimeLost,
  getShiftPattern: typeof sharedActionCreators.GetEmployeeAbsenceShiftPattern,
  getShiftPatternOptions: typeof sharedActionCreators.GetEmployeeAbsenceShiftPatternOptions,
  getEmployeeBankHolidays: typeof sharedActionCreators.GetEmployeeBankHolidaysOptions,
  getManagerSelfServiceSettings: typeof serviceActionCreators.GetManagerSelfServiceSettings,
}>;

type Props = $Exact<{
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
  ...OwnProps,
}>;

export function EmployeeAbsencesTable(props: Props) {
  const [showConfirmDeleteAbsenceModal, setShowConfirmDeleteAbsenceModal] = useState(false);
  const [absenceToDelete, setAbsenceToDelete] = useState(null);
  const [showConfirmCancelAbsenceModal, setShowConfirmCancelAbsenceModal] = useState(false);
  const [absenceToCancel, setAbsenceToCancel] = useState(null);

  useEffect(() => {
    props.getEmployeeAbsences({
      clientId: props.clientId,
      employeeId: props.employeeId,
    });
    props.getEmployeeAbsencesSchema({
      clientId: props.clientId,
      employeeId: props.employeeId,
    });

    props.getEmployeePositions({
      clientId: props.clientId,
      employeeId: props.employeeId,
    });
    props.getEmployeeBankHolidays({
      clientId: props.clientId,
      employeeId: props.employeeId,
    });

    props.getManagerSelfServiceSettings({
      clientId: props.clientId,
    });

    props.getAbsenceCancelReasons();
  }, []);

  function showAddAbsenceModal() {
    props.toggleAddAbsenceModal({
      visible: true,
    });
  }

  function showEditAbsenceModal(absenceId: EmployeeAbsence) {
    props.getEmployeeAbsence({
      clientId: props.clientId,
      employeeId: props.employeeId,
      absenceId: absenceId,
    });

    props.toggleAddAbsenceModal({
      visible: true,
      editing: true,
    });
  }
  function toggleAbsenceTransferModal(absence: EmployeeAbsence) {
    props.toggleTransferAbsenceModal({
      visible: !props.transferAbsenceModal.visible,
      absence: absence && absence.id && absence,
    });

    props.selectEmployee(null);
    props.selectEmployeePosition(null);
  }

  function closeAddAbsenceModal() {
    props.toggleAddAbsenceModal({
      visible: false,
    });
  }

  function handleFormChange({ formData }: Object) {
    props.updateAbsenceAddModalData(formData);
  }

  function handleFormSubmit(formData: Object) {
    formData?.absence?.id
      ? handleUpdateEmployeeAbsence(formData.absence)
      : handleCreateEmployeeAbsence(formData.absence);
  }

  function handleCreateEmployeeAbsence(absence: Object) {
    // when Client Admin create absence always show the reason
    if (!!props.isFirstCareUser === false) {
      absence.hasConsentToShare = true;
    }
    props.createEmployeeAbsence({
      clientId: props.clientId,
      employeeId: props.employeeId,
      formData: absence,
    });
  }

  function handleUpdateEmployeeAbsence(absence: Object) {
    props.updateEmployeeAbsence({
      clientId: props.clientId,
      employeeId: props.employeeId,
      formData: absence,
      reloadListAfterUpdate: true,
    });
  }

  function handlePaginationChange(page: number) {
    props.getEmployeeAbsences({
      clientId: props.clientId,
      employeeId: props.employeeId,
      page,
    });
  }

  function handleFiltersSubmit() {
    props.getEmployeeAbsences({
      clientId: props.clientId,
      employeeId: props.employeeId,
    });
  }

  function toggleConfirmDeleteAbsenceModal(absence: EmployeeAbsence) {
    setShowConfirmDeleteAbsenceModal(!showConfirmDeleteAbsenceModal);
    setAbsenceToDelete(absence);
  }

  function deleteAbsence(absence: EmployeeAbsence) {
    setShowConfirmDeleteAbsenceModal(!showConfirmDeleteAbsenceModal);
    setAbsenceToDelete(null);
    (props.canDeleteAbsence &&
      props.deleteEmployeeAbsence({
        clientId: props.clientId,
        employeeId: props.employeeId,
        absenceId: absenceToDelete.id,
      })) ||
      toast.error(props.t("YOU_DON_T_HAVE_PRIVILEGE_EMPLOYEE_DELETE"));
  }

  function toggleAbsenceCancelReasonsModal(absence: EmployeeAbsence) {
    setAbsenceToCancel(absence);
    setShowConfirmCancelAbsenceModal(!showConfirmCancelAbsenceModal);
  }

  function handleCancelAbsence({ cancelReason }: Object) {
    props.cancelEmployeeAbsence({
      clientId: props.clientId,
      employeeId: props.employeeId,
      absenceId: absenceToCancel.id,
      data: cancelReason,
    });

    setShowConfirmCancelAbsenceModal(!showConfirmCancelAbsenceModal);
    setAbsenceToCancel(null);
  }

  return (
    <>
      <SectionComponent wrapperClassName="flex-column" className="gs-container">
        {props.withFilters && (
          <div className="pb-4">
            <AbsencesFilter
              clientId={props.clientId}
              employee={props.employee}
              schema={props.absencesSchema}
              onSubmit={handleFiltersSubmit}
              showAddAbsenceModal={showAddAbsenceModal}
              canAddAbsence={props.canAddAbsence}
            />
          </div>
        )}
        <EmployeeAbsenceHistoryTable
          clientId={props.clientId}
          rowData={props.absences}
          absenceDetailsRoute={props.absenceDetailsRoute}
          getEmployeeAbsences={handlePaginationChange}
          pagination={props.pagination}
          employeeId={props.employeeId}
          showEditAbsenceModal={showEditAbsenceModal}
          showAbsenceTransferModal={toggleAbsenceTransferModal}
          confirmDeleteAbsence={toggleConfirmDeleteAbsenceModal}
          canEditAbsence={props.canUpdateAbsence}
          canDeleteAbsence={props.canDeleteAbsence}
          canCancelAbsence={props.canCancelAbsence}
          canTransferAbsence={props.canTransferAbsence}
          showAbsenceCancelReasonsModal={toggleAbsenceCancelReasonsModal}
        />
        {props.addAbsenceModal.visible && props.absencesSchema && (
          <AddEmployeeAbsenceModal
            onDismissHandler={closeAddAbsenceModal}
            onChange={handleFormChange}
            onSubmit={handleFormSubmit}
            editing={props.addAbsenceModal.editing}
            schema={props.absencesSchema}
            absence={props.absence}
            timeLostShiftPattern={props.absenceShiftPattern}
            absenceId={null}
            employee={props.employee}
            clientId={props.clientId}
            employeePositions={props.employeePositions}
          />
        )}
        {props.transferAbsenceModal.visible && (
          <TransferAbsenceModal
            onDismissHandler={toggleAbsenceTransferModal}
            onChange={handleFormChange}
            onSubmit={handleFormSubmit}
            absence={props.transferAbsenceModal.absence}
            employee={props.employee}
            clientId={props.clientId}
          />
        )}
      </SectionComponent>
      {showConfirmDeleteAbsenceModal && (
        <ConfirmationModal
          headerText={props.t("ARE_YOU_SURE")}
          bodyContent={props.t("YOU_SURE_WANT_DELETE_ASBSENCE_DETAILED_DESCRIPTION")}
          cancelBtnLabel={props.t("NO_KEEP_ABSENCE")}
          cancelBtnColor="primary"
          confirmBtnLabel={props.t("YES_DELETE_ABSENCE")}
          confirmBtnIcon="replay_circle_filled"
          confirmBtnColor="negative"
          modalSize="normal"
          onCancel={toggleConfirmDeleteAbsenceModal}
          onConfirm={deleteAbsence}
        />
      )}
      {showConfirmCancelAbsenceModal && (
        <AbsenceCancelReasonsModal
          absence={absenceToCancel}
          absenceCancelReasons={props.absenceCancelReasons || []}
          onSubmit={handleCancelAbsence}
          onCancel={toggleAbsenceCancelReasonsModal}
        />
      )}
    </>
  );
}

const mapStateToProps = (state: State, ownProps: Object): PropsFromState => {
  return {
    absence: selectors.getAbsenceDetails(state),
    absences: selectors.getAbsences(state),
    absenceShiftPattern: selectors.getAbsenceShiftPattern(state),
    addAbsenceModal: selectors.getAddAbsenceModal(state),
    transferAbsenceModal: selectors.getTransferAbsenceModal(state),
    absencesSchema: selectors.getAbsencesSchema(state),
    pagination: selectors.getAbsencesPagination(state),
    employee: getEmployee(ownProps.employeeId)(state),
    employeePositions: getEmployeePositions(state),
    isFirstCareUser: accountSelectors.isFirstCareUser(state),
    currentUserType: accountSelectors.getUserType(state),
    currentUserPermissions: accountSelectors.getPermissions(state),
    absenceCancelReasons: selectors.selectAbsenceCancelReasons(state),
    managerSelfServiceSettings: selectManagerSelfServiceSettings(state),
    canAddAbsence: accountSelectors.hasPermissionAbsenceCreate(state),
    canUpdateAbsence: accountSelectors.hasPermissionAbsenceUpdate(state),
    canCancelAbsence: accountSelectors.hasPermissionAbsenceCancel(state),
    canDeleteAbsence: accountSelectors.hasPermissionAbsenceDelete(state),
    canTransferAbsence: accountSelectors.hasPermissionAbsenceTransfer(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getEmployeePositions: sharedActionCreators.GetSharedEmployeePositions,
  getEmployeeAbsences: sharedActionCreators.GetEmployeeAbsences,
  deleteEmployeeAbsence: sharedActionCreators.DeleteEmployeeAbsence,
  cancelEmployeeAbsence: sharedActionCreators.CancelEmployeeAbsence,
  getAbsenceCancelReasons: sharedActionCreators.GetAbsenceCancelReasons,
  toggleAddAbsenceModal: sharedActionCreators.ToggleAddAbsenceModal,
  toggleTransferAbsenceModal: sharedActionCreators.ToggleTransferAbsenceModal,
  updateAbsenceAddModalData: sharedActionCreators.UpdateAbsenceAddModalData,
  getEmployeeAbsencesSchema: sharedActionCreators.GetEmployeeAbsencesSchema,
  createEmployeeAbsence: sharedActionCreators.CreateEmployeeAbsence,
  updateEmployeeAbsence: sharedActionCreators.UpdateEmployeeAbsence,
  selectEmployee: sharedActionCreators.SelectEmployee,
  selectEmployeePosition: sharedActionCreators.SelectEmployeePosition,

  getEmployeeAbsence: sharedActionCreators.GetEmployeeAbsence,
  getAbsenceTimeLost: sharedActionCreators.GetAbsenceTimeLost,
  getShiftPattern: sharedActionCreators.GetEmployeeAbsenceShiftPattern,
  getShiftPatternOptions: sharedActionCreators.GetEmployeeAbsenceShiftPatternOptions,
  getEmployeeBankHolidays: sharedActionCreators.GetEmployeeBankHolidaysOptions,
  getManagerSelfServiceSettings: serviceActionCreators.GetManagerSelfServiceSettings,
};

export default connect(mapStateToProps, mapDispatchToProps)(localize()(EmployeeAbsencesTable));
